import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid, Paper, Typography, TextField, Button,
  FormControl, InputLabel, Select, MenuItem,
  Chip, OutlinedInput, Checkbox, ListItemText,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel,
  InputAdornment, CircularProgress, Divider,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  Legend, Cell, LabelList,
} from "recharts";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

// ─────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────
const CRM_STATUS_ORDER = [
  "Não iniciado", "Contato", "Em andamento",
  "Proposta", "Follow up", "Sucesso", "Perdido",
];

const STATUS_COLORS = {
  "Não iniciado": "#9e9e9e",
  "Contato":       "#2196f3",
  "Em andamento":  "#ff9800",
  "Proposta":      "#9c27b0",
  "Follow up":     "#00bcd4",
  "Sucesso":       "#4caf50",
  "Perdido":       "#f44336",
};

const CHART_COLORS = [
  "#2196f3","#4caf50","#ff9800","#9c27b0",
  "#00bcd4","#f44336","#795548","#607d8b",
  "#e91e63","#ff5722",
];

// ─────────────────────────────────────────────────────────
// Estilos
// ─────────────────────────────────────────────────────────
const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
    overflowY: "auto",
    flex: 1,
    minHeight: 0,
  },
  filterPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 10,
  },
  statCard: {
    padding: theme.spacing(2),
    borderRadius: 10,
    textAlign: "center",
    borderTop: "4px solid",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    boxSizing: "border-box",
  },
  statValue: { fontSize: "1.7rem", fontWeight: 800, lineHeight: 1.2 },
  statLabel: { fontSize: "0.72rem", color: "#666", marginTop: 4 },
  chartPaper: {
    padding: theme.spacing(2),
    borderRadius: 10,
    marginBottom: theme.spacing(2),
  },
  chartTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
    fontSize: "0.9rem",
    color: "#444",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  tableContainer: { maxHeight: 420, borderRadius: 8, marginTop: theme.spacing(1) },
  tableHead: { background: theme.palette.primary.main },
  tableHeadCell: {
    color: "#fff",
    fontWeight: 700,
    whiteSpace: "nowrap",
    background: theme.palette.primary.main,
    "& .MuiTableSortLabel-root": { color: "#fff" },
    "& .MuiTableSortLabel-root:hover": { color: "#fff" },
    "& .MuiTableSortLabel-root.MuiTableSortLabel-active": { color: "#fff" },
    "& .MuiTableSortLabel-icon": { color: "#fff !important" },
  },
  tableRow: { "&:nth-of-type(odd)": { background: "#f9f9f9" } },
  chip: { margin: 2, borderRadius: 20, fontSize: "0.72rem" },
  filterRow: { display: "flex", gap: theme.spacing(1.5), flexWrap: "wrap", alignItems: "flex-end" },
  searchInput: { minWidth: 220 },
  multiSelect: { minWidth: 200 },
  exportBtn: { marginLeft: "auto" },
  noData: { textAlign: "center", color: "#999", padding: theme.spacing(4) },
}));

// ─────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────
const formatCurrency = v =>
  v != null ? `R$ ${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}` : "—";

const formatDate = d => {
  if (!d) return "—";
  try { return format(parseISO(d.substring(0, 10) + "T12:00:00"), "dd/MM/yyyy"); }
  catch { return d; }
};

const safeParseDate = d => {
  if (!d) return null;
  try { return parseISO(d.substring(0, 10) + "T12:00:00"); }
  catch { return null; }
};

// ─────────────────────────────────────────────────────────
// Custom tooltip para recharts
// ─────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: "8px 14px", fontSize: 13 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color }}>
          {p.name}: <strong>{p.value}</strong>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────
const CrmRelatorios = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  // ── Filtros ──
  const [dateStart, setDateStart] = useState(() => {
    const d = new Date();
    d.setMonth(0, 1);
    return format(d, "yyyy-MM-dd");
  });
  const [dateEnd, setDateEnd] = useState(format(new Date(), "yyyy-MM-dd"));
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  // ── Dados ──
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Tabela ──
  const [tableSearch, setTableSearch] = useState("");
  const [orderBy, setOrderBy] = useState("dataInicio");
  const [orderDir, setOrderDir] = useState("desc");

  // ── Carregar usuários ──
  useEffect(() => {
    api.get("/users", { params: { all: true } })
      .then(({ data }) => setAllUsers(data.users || []))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ticket/crm-report", {
        params: {
          dateStart: dateStart || undefined,
          dateEnd: dateEnd || undefined,
          userIds: selectedUsers.length ? JSON.stringify(selectedUsers) : undefined,
          crmStatuses: selectedStatuses.length ? JSON.stringify(selectedStatuses) : undefined,
        },
      });
      setTickets(data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [dateStart, dateEnd, selectedUsers, selectedStatuses]);

  useEffect(() => { fetchData(); }, []);

  // ─────────────────────────────────────────────────────────
  // Derived data para gráficos
  // ─────────────────────────────────────────────────────────

  // Highlights por status
  const statusCounts = useMemo(() => {
    const map = {};
    CRM_STATUS_ORDER.forEach(s => { map[s] = 0; });
    tickets.forEach(t => {
      const s = t.crmStatus || "Não iniciado";
      map[s] = (map[s] || 0) + 1;
    });
    return map;
  }, [tickets]);

  // Gráfico de linha: leads por data de início (agrupado por dia)
  const lineData = useMemo(() => {
    const map = {};
    tickets.forEach(t => {
      const d = t.dataInicio || t.createdAt?.substring(0, 10);
      if (!d) return;
      const key = d.substring(0, 10);
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, count]) => ({
        date: format(parseISO(date + "T12:00:00"), "dd/MM"),
        count,
      }));
  }, [tickets]);

  // Gráfico de barras verticais: volume por status (ordem do funil)
  const statusBarData = useMemo(() =>
    CRM_STATUS_ORDER.map(s => ({
      status: s,
      quantidade: statusCounts[s] || 0,
      color: STATUS_COLORS[s],
    })),
  [statusCounts]);

  // Top 10 motivos de perda (horizontal)
  const motivoPerdaData = useMemo(() => {
    const map = {};
    tickets
      .filter(t => t.motivoPerda)
      .forEach(t => { map[t.motivoPerda] = (map[t.motivoPerda] || 0) + 1; });
    return Object.entries(map)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([motivo, count]) => ({ motivo: motivo.length > 40 ? motivo.substring(0, 38) + "…" : motivo, count }));
  }, [tickets]);

  // Status x Valor (vertical)
  const statusValorData = useMemo(() =>
    CRM_STATUS_ORDER.map(s => {
      const soma = tickets
        .filter(t => (t.crmStatus || "Não iniciado") === s && t.valorVenda)
        .reduce((acc, t) => acc + Number(t.valorVenda), 0);
      return { status: s, valor: Math.round(soma), color: STATUS_COLORS[s] };
    }),
  [tickets]);

  // Atendentes x Volume de cards (horizontal)
  const atendenteVolumeData = useMemo(() => {
    const map = {};
    tickets.forEach(t => {
      const nome = t.user?.name || "Sem atendente";
      map[nome] = (map[nome] || 0) + 1;
    });
    return Object.entries(map)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 15)
      .map(([name, count]) => ({ name, count }));
  }, [tickets]);

  // Atendentes x Sucesso/Perda com percentual (vertical)
  const atendenteResultadoData = useMemo(() => {
    const map = {};
    tickets.forEach(t => {
      const nome = t.user?.name || "Sem atendente";
      if (!map[nome]) map[nome] = { sucesso: 0, perdido: 0, total: 0 };
      map[nome].total++;
      if (t.crmStatus === "Sucesso") map[nome].sucesso++;
      if (t.crmStatus === "Perdido") map[nome].perdido++;
    });
    return Object.entries(map)
      .filter(([, v]) => v.sucesso + v.perdido > 0)
      .sort(([, a], [, b]) => (b.sucesso + b.perdido) - (a.sucesso + a.perdido))
      .slice(0, 12)
      .map(([name, v]) => ({
        name,
        Sucesso: v.sucesso,
        Perdido: v.perdido,
        "% Sucesso": v.total > 0 ? Math.round((v.sucesso / v.total) * 100) : 0,
      }));
  }, [tickets]);

  // ─────────────────────────────────────────────────────────
  // Tabela
  // ─────────────────────────────────────────────────────────
  const filteredTableTickets = useMemo(() => {
    const q = tableSearch.toLowerCase().trim();
    let rows = tickets.filter(t => {
      if (!q) return true;
      const name = t.contact?.name?.toLowerCase() || "";
      const emp = t.contact?.razaoSocial?.toLowerCase() || "";
      const cid = t.contact?.cidade?.toLowerCase() || "";
      const est = t.contact?.estado?.toLowerCase() || "";
      const sta = (t.crmStatus || "").toLowerCase();
      const di = formatDate(t.dataInicio);
      const df = formatDate(t.dataFim);
      return name.includes(q) || emp.includes(q) || cid.includes(q) ||
             est.includes(q) || sta.includes(q) || di.includes(q) || df.includes(q);
    });

    rows = [...rows].sort((a, b) => {
      let va, vb;
      switch (orderBy) {
        case "name": va = a.contact?.name || ""; vb = b.contact?.name || ""; break;
        case "cidade": va = a.contact?.cidade || ""; vb = b.contact?.cidade || ""; break;
        case "estado": va = a.contact?.estado || ""; vb = b.contact?.estado || ""; break;
        case "razaoSocial": va = a.contact?.razaoSocial || ""; vb = b.contact?.razaoSocial || ""; break;
        case "segmento": va = a.contact?.segmento || ""; vb = b.contact?.segmento || ""; break;
        case "crmStatus": va = a.crmStatus || ""; vb = b.crmStatus || ""; break;
        case "dataInicio":
          va = a.dataInicio || ""; vb = b.dataInicio || ""; break;
        case "dataFim":
          va = a.dataFim || ""; vb = b.dataFim || ""; break;
        case "valorVenda":
          va = Number(a.valorVenda) || 0; vb = Number(b.valorVenda) || 0;
          return orderDir === "asc" ? va - vb : vb - va;
        default: va = ""; vb = "";
      }
      return orderDir === "asc"
        ? String(va).localeCompare(String(vb), "pt-BR")
        : String(vb).localeCompare(String(va), "pt-BR");
    });

    return rows;
  }, [tickets, tableSearch, orderBy, orderDir]);

  const handleSort = col => {
    if (orderBy === col) {
      setOrderDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(col);
      setOrderDir("asc");
    }
  };

  // ─────────────────────────────────────────────────────────
  // Export CSV
  // ─────────────────────────────────────────────────────────
  const exportCsv = () => {
    const headers = ["Nome", "Cidade", "Estado", "WhatsApp", "Razão Social", "Segmento", "Status", "Data Início", "Data Limite", "Valor", "Atendente"];
    const rows = filteredTableTickets.map(t => [
      t.contact?.name || "",
      t.contact?.cidade || "",
      t.contact?.estado || "",
      t.contact?.number || "",
      t.contact?.razaoSocial || "",
      t.contact?.segmento || "",
      t.crmStatus || "",
      formatDate(t.dataInicio),
      formatDate(t.dataFim),
      t.valorVenda != null ? Number(t.valorVenda).toFixed(2) : "",
      t.user?.name || "",
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `crm-relatorio-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // ─────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────
  const totalValor = tickets.reduce((acc, t) => acc + (Number(t.valorVenda) || 0), 0);

  return (
    <MainContainer>
      <MainHeader>
        <Title>Relatórios CRM</Title>
      </MainHeader>

      <div className={classes.root}>
        {/* ── Filtros ── */}
        <Paper className={classes.filterPaper} elevation={1}>
          <div className={classes.filterRow}>
            <TextField
              label="Data início"
              type="date"
              value={dateStart}
              onChange={e => setDateStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
            />
            <TextField
              label="Data fim"
              type="date"
              value={dateEnd}
              onChange={e => setDateEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
            />

            <FormControl variant="outlined" size="small" className={classes.multiSelect}>
              <InputLabel>Atendentes</InputLabel>
              <Select
                multiple
                value={selectedUsers}
                onChange={e => setSelectedUsers(e.target.value)}
                input={<OutlinedInput labelWidth={80} />}
                renderValue={sel =>
                  sel.map(id => allUsers.find(u => u.id === id)?.name || id).join(", ")
                }
              >
                {allUsers.map(u => (
                  <MenuItem key={u.id} value={u.id}>
                    <Checkbox checked={selectedUsers.includes(u.id)} size="small" />
                    <ListItemText primary={u.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl variant="outlined" size="small" className={classes.multiSelect}>
              <InputLabel>Status</InputLabel>
              <Select
                multiple
                value={selectedStatuses}
                onChange={e => setSelectedStatuses(e.target.value)}
                input={<OutlinedInput labelWidth={45} />}
                renderValue={sel => sel.join(", ")}
              >
                {CRM_STATUS_ORDER.map(s => (
                  <MenuItem key={s} value={s}>
                    <Checkbox checked={selectedStatuses.includes(s)} size="small" />
                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: STATUS_COLORS[s], display: "inline-block", marginRight: 8 }} />
                    <ListItemText primary={s} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="primary"
              onClick={fetchData}
              disabled={loading}
              style={{ borderRadius: 8, minWidth: 100 }}
            >
              {loading ? <CircularProgress size={18} /> : "Buscar"}
            </Button>
          </div>
        </Paper>

        {/* ── Highlights por status ── */}
        <Grid container spacing={2} style={{ marginBottom: 16, flexWrap: "nowrap" }}>
          {CRM_STATUS_ORDER.map(s => (
            <Grid item key={s} style={{ flex: 1, display: "flex", minWidth: 0 }}>
              <Paper className={classes.statCard} style={{ borderTopColor: STATUS_COLORS[s], width: "100%" }} elevation={1}>
                <Typography className={classes.statValue} style={{ color: STATUS_COLORS[s] }}>
                  {statusCounts[s] || 0}
                </Typography>
                <Typography className={classes.statLabel}>{s}</Typography>
              </Paper>
            </Grid>
          ))}
          <Grid item style={{ flex: 1, display: "flex", minWidth: 0 }}>
            <Paper className={classes.statCard} style={{ borderTopColor: "#1565c0", width: "100%" }} elevation={1}>
              <Typography className={classes.statValue} style={{ color: "#1565c0", fontSize: "1.1rem" }}>
                {formatCurrency(totalValor)}
              </Typography>
              <Typography className={classes.statLabel}>Valor Total</Typography>
            </Paper>
          </Grid>
          <Grid item style={{ flex: 1, display: "flex", minWidth: 0 }}>
            <Paper className={classes.statCard} style={{ borderTopColor: "#455a64", width: "100%" }} elevation={1}>
              <Typography className={classes.statValue} style={{ color: "#455a64" }}>
                {tickets.length}
              </Typography>
              <Typography className={classes.statLabel}>Total de Cards</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* ── Linha: Leads por data de início ── */}
        <Paper className={classes.chartPaper} elevation={1}>
          <Typography className={classes.chartTitle}>Leads Entrando por Data de Início</Typography>
          {lineData.length === 0 ? (
            <Typography className={classes.noData}>Sem dados no período</Typography>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={lineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <ReTooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="count" name="Leads" stroke="#2196f3" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Paper>

        <Grid container spacing={2}>
          {/* ── Barras: Volume x Status (funil) ── */}
          <Grid item xs={12} md={6}>
            <Paper className={classes.chartPaper} elevation={1}>
              <Typography className={classes.chartTitle}>Volume de Cards por Status (Funil)</Typography>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={statusBarData} barCategoryGap="30%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="status" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <ReTooltip content={<CustomTooltip />} />
                  <Bar dataKey="quantidade" name="Cards" radius={[4, 4, 0, 0]}>
                    {statusBarData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                    <LabelList dataKey="quantidade" position="top" style={{ fontSize: 11, fontWeight: 700 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* ── Barras: Status x Valor do negócio ── */}
          <Grid item xs={12} md={6}>
            <Paper className={classes.chartPaper} elevation={1}>
              <Typography className={classes.chartTitle}>Status × Valor do Negócio (R$)</Typography>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={statusValorData} barCategoryGap="30%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="status" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} />
                  <ReTooltip formatter={v => formatCurrency(v)} content={<CustomTooltip />} />
                  <Bar dataKey="valor" name="Valor (R$)" radius={[4, 4, 0, 0]}>
                    {statusValorData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* ── Top 10 Motivos de Perda (horizontal) ── */}
          <Grid item xs={12} md={6}>
            <Paper className={classes.chartPaper} elevation={1}>
              <Typography className={classes.chartTitle}>Top 10 Motivos de Perda</Typography>
              {motivoPerdaData.length === 0 ? (
                <Typography className={classes.noData}>Sem registros de perda</Typography>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(220, motivoPerdaData.length * 36)}>
                  <BarChart data={motivoPerdaData} layout="vertical" barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="motivo" tick={{ fontSize: 10 }} width={200} />
                    <ReTooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Ocorrências" fill="#f44336" radius={[0, 4, 4, 0]}>
                      <LabelList dataKey="count" position="right" style={{ fontSize: 11, fontWeight: 700 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          {/* ── Atendentes x Volume de cards (horizontal) ── */}
          <Grid item xs={12} md={6}>
            <Paper className={classes.chartPaper} elevation={1}>
              <Typography className={classes.chartTitle}>Atendentes × Volume de Cards</Typography>
              {atendenteVolumeData.length === 0 ? (
                <Typography className={classes.noData}>Sem dados</Typography>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(220, atendenteVolumeData.length * 36)}>
                  <BarChart data={atendenteVolumeData} layout="vertical" barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
                    <ReTooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Cards" radius={[0, 4, 4, 0]}>
                      {atendenteVolumeData.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                      <LabelList dataKey="count" position="right" style={{ fontSize: 11, fontWeight: 700 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          {/* ── Atendentes x Sucesso/Perdido com % em linha (vertical) ── */}
          <Grid item xs={12}>
            <Paper className={classes.chartPaper} elevation={1}>
              <Typography className={classes.chartTitle}>Atendentes × Resultados (Sucesso / Perdido) com % de Sucesso</Typography>
              {atendenteResultadoData.length === 0 ? (
                <Typography className={classes.noData}>Sem dados de resultado</Typography>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={atendenteResultadoData} barCategoryGap="25%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={50} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit="%" domain={[0, 100]} />
                    <ReTooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="Sucesso" fill="#4caf50" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="left" dataKey="Perdido" fill="#f44336" radius={[4, 4, 0, 0]} />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="% Sucesso"
                      stroke="#1565c0"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      name="% Sucesso"
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* ── Tabela relacional ── */}
        <Paper className={classes.chartPaper} elevation={1}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
            <Typography className={classes.chartTitle} style={{ marginBottom: 0 }}>
              Tabela de Cards ({filteredTableTickets.length})
            </Typography>
            <TextField
              placeholder="Buscar por nome, empresa, cidade, estado, data ou status..."
              value={tableSearch}
              onChange={e => setTableSearch(e.target.value)}
              variant="outlined"
              size="small"
              className={classes.searchInput}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ),
              }}
              style={{ marginLeft: "auto" }}
            />
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<GetAppIcon />}
              onClick={exportCsv}
              style={{ borderRadius: 8, fontWeight: 600, boxShadow: "0 2px 6px rgba(0,0,0,0.2)" }}
            >
              Exportar CSV
            </Button>
          </div>

          <TableContainer className={classes.tableContainer}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    { id: "name", label: "Nome" },
                    { id: "cidade", label: "Cidade" },
                    { id: "estado", label: "UF" },
                    { id: "number", label: "WhatsApp", noSort: true },
                    { id: "razaoSocial", label: "Razão Social" },
                    { id: "segmento", label: "Segmento" },
                    { id: "crmStatus", label: "Status" },
                    { id: "dataInicio", label: "Data Início" },
                    { id: "dataFim", label: "Data Limite" },
                    { id: "valorVenda", label: "Valor" },
                    { id: "user", label: "Atendente", noSort: true },
                  ].map(col => (
                    <TableCell key={col.id} className={classes.tableHeadCell}>
                      {col.noSort ? col.label : (
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? orderDir : "asc"}
                          onClick={() => handleSort(col.id)}
                          hideSortIcon={false}
                        >
                          {col.label}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTableTickets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className={classes.noData}>
                      {loading ? "Carregando..." : "Nenhum dado encontrado"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTableTickets.map(t => (
                    <TableRow key={t.id} className={classes.tableRow} hover>
                      <TableCell>{t.contact?.name || "—"}</TableCell>
                      <TableCell>{t.contact?.cidade || "—"}</TableCell>
                      <TableCell>{t.contact?.estado || "—"}</TableCell>
                      <TableCell>{t.contact?.number || "—"}</TableCell>
                      <TableCell>{t.contact?.razaoSocial || "—"}</TableCell>
                      <TableCell>{t.contact?.segmento || "—"}</TableCell>
                      <TableCell>
                        <Chip
                          label={t.crmStatus || "Não iniciado"}
                          size="small"
                          className={classes.chip}
                          style={{
                            background: STATUS_COLORS[t.crmStatus] || "#9e9e9e",
                            color: "#fff",
                          }}
                        />
                      </TableCell>
                      <TableCell>{formatDate(t.dataInicio)}</TableCell>
                      <TableCell>{formatDate(t.dataFim)}</TableCell>
                      <TableCell>{formatCurrency(t.valorVenda)}</TableCell>
                      <TableCell>{t.user?.name || "—"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </div>
    </MainContainer>
  );
};

export default CrmRelatorios;
